import type { ChatSuggestion } from '../chat/chat-types';
import type { SignupAccountType, SignupDetails, SignupDetailsErrors } from './signup-validation';
import { useState } from 'react';
import { useAuth } from 'hooks/useAuth';
import { trackUmami } from '@/lib/umami/umami';
import { UMAMI_ACCOUNT_TYPES, umamiLabel } from '@/lib/umami/umami-events';
import { PACES } from '../chat/chat-pacing';
import { useChat } from '../chat/use-chat';
import { CREATE_ACCOUNT_LABEL, FORM_INTRO_MESSAGES, SIGNUP_CHAPTER, TYPE_QUESTION_LINE } from './signup-script';
import { EMPTY_SIGNUP_DETAILS, validateSignupDetails } from './signup-validation';

/**
 * pitch    the sales conversation, with the persistent "Create your account" button
 * intro    the button was pressed and Pertento is leading into the form
 * form     the details card is in the thread
 * type     the client / agency cards are in the thread
 * sending  the account is being created
 * done     the confirmation screen
 */
export type SignupStage = 'pitch' | 'intro' | 'form' | 'type' | 'sending' | 'done';

/** How long the chat takes to leave before the confirmation screen mounts. */
export const LEAVE_DURATION = 620;

const STEP_BY_STAGE: Partial<Record<SignupStage, number>> = { form: 1, type: 2, sending: 2 };

const readErrorMessage = (ex: unknown): string => {
  const { message } = (ex ?? {}) as { message?: string };
  return message || 'Something went wrong on our side.';
};

/**
 * The signup conversation: pitch, details, client or agency, confirmation.
 *
 * The account is created at the very end, when the visitor picks client or agency,
 * not when they submit the form. The type is part of the signup payload and the
 * server rejects a request without it, so the form alone has nothing to send. If
 * the server refuses (an address already registered, a password rule the form did
 * not catch), the visitor is put back on the form with what they typed intact.
 */
export const useSignupChat = () => {
  // Quick: someone who has opened signup has decided and is waiting for the form.
  const chat = useChat({ pace: 'quick' });
  const { say, reply, begin, clear, stop, later } = chat;
  const { signup } = useAuth();
  const [stage, setStage] = useState<SignupStage>('pitch');
  const [details, setDetails] = useState<SignupDetails>(EMPTY_SIGNUP_DETAILS);
  const [errors, setErrors] = useState<SignupDetailsErrors>({});
  const [asked, setAsked] = useState<readonly string[]>([]);
  const [leaving, setLeaving] = useState(false);

  /** Starts over from the pitch. Called each time the signup screen opens. */
  const start = () => {
    stop();
    clear();
    setStage('pitch');
    setDetails(EMPTY_SIGNUP_DETAILS);
    setErrors({});
    setAsked([]);
    setLeaving(false);
    begin({ delay: PACES.quick.first, then: () => say(SIGNUP_CHAPTER.messages) });
  };

  const ask = (question: string) => {
    const match = SIGNUP_CHAPTER.questions.find((item) => item.question === question);
    if (!match) return;
    trackUmami('signup-question-asked', { question: umamiLabel(question) });
    setAsked((current) => [...current, question]);
    reply({ text: question, then: () => say(match.answer) });
  };

  const beginSignup = () => {
    if (stage !== 'pitch') return;
    // Allowed mid-pitch: the button is always there, and someone who has already
    // decided should not have to wait for the sales answer to finish typing.
    stop();
    setStage('intro');
    // Client or agency is not known yet: it is the last question.
    trackUmami('signup-started');
    reply({
      text: CREATE_ACCOUNT_LABEL,
      then: () => say(FORM_INTRO_MESSAGES, { onDone: () => setStage('form') }),
    });
  };

  const setField = (name: keyof SignupDetails, value: string) => {
    setDetails((current) => ({ ...current, [name]: value }));
    // Only the field being edited loses its error; the rest still stand until submit.
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const submitDetails = () => {
    const found = validateSignupDetails(details);
    if (Object.values(found).some(Boolean)) {
      // Which fields, never what was typed in them: where people trip is what the form needs.
      const fields = Object.keys(found)
        .filter((key) => found[key as keyof SignupDetailsErrors])
        .sort()
        .join(',');
      trackUmami('signup-details-invalid', { fields: umamiLabel(fields) });
      setErrors(found);
      return;
    }

    trackUmami('signup-details-submitted');

    const { first, last, email, company } = details;
    setStage('type');
    reply({
      text: `${first.trim()} ${last.trim()} · ${email.trim()} · ${company.trim()}`,
      then: () =>
        say([
          { kind: 'text', from: 'pt', text: `Thanks, ${first.trim()}.` },
          { kind: 'headline', text: `Is ${company.trim()} a client or an agency?` },
          { kind: 'text', from: 'pt', text: TYPE_QUESTION_LINE },
        ]),
    });
  };

  const createAccount = async (accountType: SignupAccountType) => {
    try {
      await signup({
        email: details.email.trim(),
        firstName: details.first.trim(),
        lastName: details.last.trim(),
        password: details.password,
        confirmPassword: details.confirm,
        companyName: details.company.trim(),
        companyType: accountType,
      });
    } catch (ex) {
      trackUmami('signup-failed', { type: UMAMI_ACCOUNT_TYPES[accountType] });
      say([{ kind: 'text', from: 'pt', text: `That did not go through. ${readErrorMessage(ex)}` }], {
        onDone: () => setStage('form'),
      });
      return;
    }

    // After the server has accepted it, never on the button press.
    trackUmami('signup-completed', { type: UMAMI_ACCOUNT_TYPES[accountType] });
    setLeaving(true);
    later(() => {
      setStage('done');
      // The panel refuses to close while an account is being created, and `leaving` is part
      // of that. Left on, the confirmation's "Back to sign in" links were locked out.
      setLeaving(false);
    }, LEAVE_DURATION);
  };

  const chooseType = (accountType: SignupAccountType) => {
    if (stage !== 'type' || chat.busy) return;
    setStage('sending');
    reply({ text: accountType === 'Agency' ? 'An agency' : 'A client', then: () => void createAccount(accountType) });
  };

  const suggestions: ChatSuggestion[] =
    stage === 'pitch' && !chat.busy && chat.thread.length > 0
      ? SIGNUP_CHAPTER.questions
          .filter((item) => !asked.includes(item.question))
          .map((item) => ({ label: item.question, onPick: () => ask(item.question) }))
      : [];

  return {
    chat,
    stage,
    step: STEP_BY_STAGE[stage] ?? null,
    details,
    errors,
    suggestions,
    leaving,
    showForm: stage === 'form' && !chat.busy,
    showTypeCards: stage === 'type' && !chat.busy,
    canBegin: stage === 'pitch',
    start,
    beginSignup,
    setField,
    submitDetails,
    chooseType,
  };
};
